import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Save, Lock, CreditCard, Plane, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import ProGate from "../components/ProGate";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import { useToast } from "../hooks/use-toast";
import { AIRLINES, TRANSFER_PARTNERS, fmtPoints } from "../data/mock";

function BalanceField({ id, label, sub, value, onChange, color }) {
  return (
    <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ background: color || "#2563eb" }} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-900">{label}</span>
        {sub && <span className="block truncate text-xs text-slate-500">{sub}</span>}
      </span>
      <input type="number" min={0} step={1000} inputMode="numeric" value={value ?? ""} onChange={(e) => onChange(id, e.target.value)} placeholder="0" data-testid={`wallet-input-${id}`} className="h-10 w-28 rounded-lg border border-slate-200 px-2 text-right text-base font-semibold outline-none focus:border-blue-500 sm:w-32 sm:text-sm" />
    </label>
  );
}

export default function WalletPage() {
  const { user } = useAuth();
  const wallet = useWallet();
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(wallet?.balances || {}); }, [wallet?.balances]);

  const set = (id, v) => setForm((f) => ({ ...f, [id]: v === "" ? "" : Number(v) }));
  const total = Object.values(form).reduce((s, v) => s + (Number(v) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(Object.entries(form).filter(([, v]) => Number(v) > 0));
      await wallet.save(clean);
      toast({ title: "Wallet saved", description: "Every deal now shows whether you can already afford it." });
    } catch { toast({ title: "Could not save wallet", variant: "destructive" }); } finally { setSaving(false); }
  };

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Points Wallet | FindMyFlight" description="Enter your credit-card and airline points balances and see instantly which award deals you can already afford." path="/wallet" />
      <div className="mx-auto max-w-4xl px-5 py-10">
        <div className="flex items-center gap-2 text-blue-600"><Wallet className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Points Wallet</span><span className="rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">PRO</span></div>
        <h1 className="mt-2 text-3xl font-extrabold text-slate-900">What can you already afford?</h1>
        <p className="mt-2 max-w-2xl text-slate-600">Enter your balances once. Every deal card will show a green <b>You can afford this</b> badge when your program balance — or any card currency that transfers to it — covers the points.</p>

        {!user ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="wallet-signin">
            <Lock className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-700">Sign in to build your wallet</p>
            <div className="mt-4 flex justify-center gap-2"><Link to="/login"><Button variant="outline">Login</Button></Link><Link to="/register"><Button className="bg-blue-600 hover:bg-blue-700">Create free account</Button></Link></div>
          </div>
        ) : (
          <ProGate locked={!user.pro} title="Points Wallet is a PRO feature" description="PRO members store their card and airline balances once, and every deal instantly shows whether they can already afford it, plus a full year of availability, Cabin Compare, alerts and live flight tracking." className="mt-8">
          <div className="grid gap-8 lg:grid-cols-5">
            <div className="space-y-6 lg:col-span-3">
              <section>
                <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><CreditCard className="h-4 w-4 text-blue-600" /> Transferable card points</h2>
                <div className="mt-3 grid gap-2 sm:grid-cols-1">
                  {Object.entries(TRANSFER_PARTNERS).map(([k, p]) => <BalanceField key={k} id={k} label={p.name} value={form[k]} onChange={set} color={p.color} />)}
                </div>
              </section>
              <section>
                <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><Plane className="h-4 w-4 text-blue-600" /> Airline programs</h2>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {Object.entries(AIRLINES).map(([code, a]) => <BalanceField key={code} id={code} label={a.program} sub={a.name} value={form[code]} onChange={set} color="#94a3b8" />)}
                </div>
              </section>
            </div>
            <aside className="lg:col-span-2">
              <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total across programs</p>
                <p className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="wallet-total">{fmtPoints(total)} <span className="text-sm font-semibold text-slate-500">pts</span></p>
                <Button onClick={save} disabled={saving} data-testid="wallet-save-button" className="mt-5 w-full gap-2 bg-blue-600 font-semibold hover:bg-blue-700"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save wallet"}</Button>
                <ul className="mt-5 space-y-2 text-xs text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" /> Balances are private to your account.</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" /> Card points count toward every airline they transfer to (1:1 assumed).</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" /> Deals you can't afford show exactly how many points you're short.</li>
                </ul>
                <Link to="/search" className="mt-5 block"><Button variant="outline" className="w-full">Find deals I can afford</Button></Link>
              </div>
            </aside>
          </div>
          </ProGate>
        )}
      </div>
    </main>
  );
}
